import { MELEE } from "@eldrun/content";
import { EAT, MOVE } from "@eldrun/sim";
import type { AVATAR_CLIPS } from "../assets/avatars.generated.ts";

/** Clipes do avatar (o contrato `AVATAR_CLIP_SPEC`, como saem do pacote). */
export type AvatarClipName = keyof typeof AVATAR_CLIPS;

/** O que a máquina sabe de cada clipe: duração e, do pacote, a velocidade dos pés e o impacto. */
export interface ClipInfo {
  readonly duration: number;
  /** Locomoção: m/s em que os pés não escorregam com o clipe a 1×. */
  readonly speed?: number;
  /** Golpe: instante (s) do impacto. */
  readonly impact?: number;
}

export type ClipTable = Readonly<Record<AvatarClipName, ClipInfo>>;

/** O que a animação lê do corpo (predito ou interpolado) e dos eventos, a cada quadro. */
export interface AvatarSignals {
  /** Velocidade horizontal (m/s). */
  speed: number;
  vy: number;
  grounded: boolean;
  crouching: boolean;
  swimming: boolean;
  sliding: boolean;
  dead: boolean;
  /** Progresso do golpe (corpo a corpo ou coleta), de 0 a 1; 0 sem golpe. */
  swing: number;
  /** Puxando a corda do arco. */
  drawing: boolean;
  /** No canal de comer (ação mantida com comida) e quantas mordidas já completou. */
  eating: boolean;
  bites: number;
  /**
   * Segundos desde o último golpe levado e desde a última volta (Cruz ou Carranca); `Infinity` sem.
   * Pelo relógio da cena, não pelo da máquina: um avatar parado pelo LOD (longe, fora da tela, sumido)
   * não toca depois um dano ou uma volta antigos, e toca a volta que ainda está acontecendo.
   */
  hurtAgo: number;
  revivedAgo: number;
}

/** Um clipe numa camada: o instante e o peso (os pesos de uma camada somam 1). */
export interface ClipSample {
  clip: AvatarClipName;
  time: number;
  weight: number;
  /**
   * A segunda ação do clipe (só nos gestos): o mesmo gesto pode entrar por cima de si mesmo (um golpe
   * seguido do outro, uma mordida nova, outro golpe levado) com a ação anterior ainda saindo.
   */
  slot?: 1;
}

/**
 * A pose do quadro: a camada de baixo (quadril e pernas) e a de cima (tronco, braços e cabeça). Sem
 * gesto, a de cima segue a de baixo; com gesto (golpe, arco, comer, dano), ela o mistura por cima.
 */
export interface AvatarMix {
  lower: ClipSample[];
  upper: ClipSample[];
  /** Estado do corpo e gesto, para os testes e o `stats()`. */
  state: BaseKind;
  gesture: Gesture | undefined;
}

export type BaseKind = "locomocao" | "agachado" | "deslizando" | "nadando" | "pulando" | "no-ar" | "aterrissando" | "morte" | "revivendo";
export type Gesture = "golpe" | "arco" | "comendo" | "dano";
/** Os gestos (clipes da metade de cima), que têm uma segunda ação (`ClipSample.slot`). */
export const GESTURES: readonly Gesture[] = ["golpe", "arco", "comendo", "dano"];

/** Transição entre estados do corpo (s); a morte e o pulo entram mais rápido. */
const FADE = 0.15;
const FAST_FADE = 0.08;
/** Entrada e saída dos gestos (s). */
export const GESTURE_IN = 0.08;
export const GESTURE_OUT = 0.2;
/**
 * Troca de um gesto para outro, ou para ele mesmo de novo (s). O golpe que entra fica inteiro no
 * máximo até a metade do caminho até o impacto (`swingClipTime`), mesmo no ciclo mais curto.
 */
export const GESTURE_SWITCH = 0.1;
/** Abaixo disto (m/s), parado. */
const IDLE_SPEED = 0.15;
/** Teto da velocidade de reprodução da locomoção: acima disso os pés escorregam, mas a perna não vira hélice. */
const MAX_RATE = 2.6;
/**
 * Piso da reprodução do agachado andando: mais devagar que isso (a menos de 60 % da velocidade do
 * clipe), ele mistura com o `agachado-parado` em vez de virar câmera lenta.
 */
const MIN_CROUCH_RATE = 0.6;
/** Tempo no ar antes do `no-ar` sem pulo (degraus e desníveis piscam o chão). */
const AIR_GRACE = 0.12;
/** Subida (m/s) que conta como pulo ao sair do chão. */
const JUMP_VY = 2;
/** No ar por mais que isto, aterrissa (se chegar devagar). */
const LAND_AIR = 0.35;
const LAND_SPEED = 1.5;
/** Do início da puxada até o arco todo puxado (s): o tempo de carga de um arco comum. */
export const DRAW_SECONDS = 0.9;
/** Pose fixa do deslize: este ponto do `agachado-parado` (fração da duração), os dois pés no chão. */
const SLIDE_POSE = 0;
/** Instante antes do fim para segurar a última pose dos clipes que não repetem. */
const END = 1e-3;

const fraction = (t: number, d: number) => (d > 0 ? t - Math.floor(t / d) * d : 0);
const clampTime = (t: number, d: number) => Math.max(0, Math.min(t, d - END));

interface Base {
  kind: BaseKind;
  since: number;
}

/** O gesto pedido agora: o clipe, a ação, desde quando e o instante do clipe no quadro. */
interface ActiveGesture {
  kind: Gesture;
  slot: 0 | 1;
  since: number;
  time: number;
  /** Progresso do golpe (0 a 1) no quadro, para ele entrar inteiro antes do impacto. */
  swing: number;
}

const total = (samples: readonly ClipSample[]) => samples.reduce((n, x) => n + x.weight, 0);

/**
 * Máquina de estados da animação do avatar (F49), pura: dos sinais do corpo e dos eventos, a pose do
 * quadro como amostras de clipes com instante e peso, sem tocar no three. A locomoção mistura parado,
 * andando e correndo pela velocidade, com a fase dos pés comum aos dois e a velocidade de reprodução
 * em que eles não escorregam (`ClipInfo.speed`); pulo, no ar e aterrissagem vêm do chão e da subida;
 * agachado anda com a velocidade e, parado, fica no `agachado-parado` (os dois misturados pela
 * velocidade, como o parado e o andando); nadando anda com a velocidade; o deslize é o agachado parado
 * congelado; morte e revivendo tocam uma vez. Golpe, arco, comer e dano são gestos na metade de cima:
 * o golpe esticado no ciclo do golpe com o impacto do clipe no do servidor (`MELEE.impactFraction`), o
 * arco puxado em `DRAW_SECONDS`, o comer uma vez por mordida e o dano a cada golpe levado. Um gesto
 * entra por cima do que estiver na metade de cima (a locomoção ou o gesto anterior, que sai segurando o
 * último instante): os pesos dos gestos somam `G` ≤ 1 e a locomoção fica com 1 − `G`.
 */
export class AvatarStateMachine {
  private readonly clips: ClipTable;
  private time = 0;
  private base: Base = { kind: "locomocao", since: 0 };
  private from: Base | undefined;
  private fadeAt = 0;
  private fadeFor = FADE;
  /** Fase dos pés (0 a 1) da locomoção e o relógio próprio do parado, do agachado e do nado. */
  private phase = 0;
  private idle = 0;
  private crouch = 0;
  private swim = 0;
  private air = 0;
  private jumped = false;
  /** O gesto pedido agora, entrando em `blendFor` segundos desde `blendAt`. */
  private active: ActiveGesture | undefined;
  /**
   * O que a camada dos gestos mostrava quando ele entrou (ou quando o pedido acabou): os gestos de
   * antes, cada um segurando o último instante, saindo na mesma transição.
   */
  private held: ClipSample[] = [];
  private blendAt = 0;
  private blendFor = GESTURE_IN;
  private bites: number | undefined;
  /** O golpe e o dano do quadro anterior: um golpe novo (o progresso volta) ou outro golpe levado recomeçam o gesto. */
  private lastSwing = 0;
  private lastHurt = Infinity;
  /** Quantas vezes entrou em cada estado do corpo e em cada gesto (para os testes e o `stats()`). */
  readonly entered: Partial<Record<BaseKind | Gesture, number>> = {};

  constructor(clips: ClipTable) {
    this.clips = clips;
  }

  step(s: AvatarSignals, dt: number): AvatarMix {
    this.time += dt;
    const t = this.time;
    // As mordidas são o contador do corpo (só valem comendo agora): começam onde estão.
    const newBite = this.bites !== undefined && s.bites !== this.bites;
    this.bites = s.bites;

    this.advanceClocks(s, dt);
    this.enter(this.chooseBase(s, dt), t);
    const fade = this.fade(t);
    if (fade >= 1) this.from = undefined;
    const lower = merge([...this.samples(this.base, s, t, fade), ...(this.from ? this.samples(this.from, s, t, 1 - fade) : [])]);
    const upper = this.upper(s, t, dt, newBite, lower);
    const top = upper.gestures.reduce<ClipSample | undefined>((best, x) => (best && best.weight >= x.weight ? best : x), undefined);
    return { lower, upper: upper.samples, state: this.base.kind, gesture: this.active?.kind ?? (top?.clip as Gesture | undefined) };
  }

  /**
   * A metade de cima: os gestos (o pedido entrando por cima dos de antes, que saem segurando o último
   * instante) e, no que sobra, a locomoção (`lower`). Ao trocar de gesto, recomeçar o mesmo ou acabar o
   * pedido, o que a camada mostrava no quadro anterior vira `held`, e a transição parte dele: nada pula.
   */
  private upper(s: AvatarSignals, t: number, dt: number, newBite: boolean, lower: readonly ClipSample[]): { samples: ClipSample[]; gestures: ClipSample[] } {
    const want = this.chooseGesture(s);
    const current = this.active?.kind;
    const again =
      want !== undefined &&
      want === current &&
      ((want === "comendo" && newBite) || (want === "golpe" && s.swing < this.lastSwing) || (want === "dano" && s.hurtAgo < this.lastHurt));
    this.lastSwing = s.swing;
    this.lastHurt = s.hurtAgo;
    if (want !== current || again) {
      // A troca aconteceu dentro deste passo: parte do que o quadro anterior mostrou.
      const from = t - dt;
      this.held = this.gestureSamples(from);
      this.blendAt = from;
      this.blendFor = want === undefined ? GESTURE_OUT : total(this.held) > 0 ? GESTURE_SWITCH : GESTURE_IN;
      this.active = undefined;
      if (want !== undefined) {
        // A ação livre do clipe (a outra, se a de sempre ainda estiver saindo); a mais leve sai de vez.
        const weightOf = (slot: 0 | 1) => this.held.filter((x) => x.clip === want && (x.slot ?? 0) === slot).reduce((n, x) => n + x.weight, 0);
        const slot: 0 | 1 = weightOf(0) <= weightOf(1) ? 0 : 1;
        this.held = this.held.filter((x) => !(x.clip === want && (x.slot ?? 0) === slot));
        this.active = { kind: want, slot, since: t, time: 0, swing: s.swing };
        this.entered[want] = (this.entered[want] ?? 0) + 1;
      }
    }
    if (this.active) {
      this.active.swing = s.swing;
      this.active.time = this.gestureClipTime(this.active, s, t);
    }
    const gestures = this.gestureSamples(t);
    if (this.fadeIn(t) >= 1) this.held = [];
    const g = total(gestures);
    if (g <= 0) return { samples: [...lower], gestures };
    return { samples: merge([...gestures, ...(g < 1 ? lower.map((x) => ({ ...x, weight: x.weight * (1 - g) })) : [])]), gestures };
  }

  /**
   * Quanto o gesto pedido já entrou (0 a 1): pelo tempo desde a troca e, no golpe, também pelo
   * progresso dele, que o põe inteiro até a metade do caminho até o impacto (no ciclo mais curto, o
   * impacto vem antes de `GESTURE_SWITCH`).
   */
  private fadeIn(t: number): number {
    const byTime = Math.min(1, Math.max(0, (t - this.blendAt) / this.blendFor));
    if (this.active?.kind !== "golpe") return byTime;
    return Math.max(byTime, Math.min(1, this.active.swing / (MELEE.impactFraction / 2)));
  }

  /** A camada dos gestos em `t`: os de antes saindo (`held` × (1 − k)) e o pedido entrando (k). */
  private gestureSamples(t: number): ClipSample[] {
    const k = this.fadeIn(t);
    const out = this.held.flatMap((x) => (x.weight * (1 - k) > 0 ? [{ ...x, weight: x.weight * (1 - k) }] : []));
    if (this.active && k > 0) out.push({ clip: this.active.kind, time: this.active.time, weight: k, ...(this.active.slot === 1 && { slot: 1 as const }) });
    return merge(out);
  }

  private advanceClocks(s: AvatarSignals, dt: number): void {
    const c = this.clips;
    this.idle += dt;
    // Ciclos por segundo: a passada do clipe na velocidade dele; misturando andar e correr, a média.
    const walk = c.andando.speed ?? 1;
    const run = c.correndo.speed ?? 3;
    const strideWalk = walk * c.andando.duration;
    const strideRun = run * c.correndo.duration;
    const toRun = Math.max(0, Math.min(1, (s.speed - walk) / (run - walk)));
    const cycles = s.speed < walk ? 1 / c.andando.duration : Math.min(MAX_RATE / c.correndo.duration, s.speed / (strideWalk + (strideRun - strideWalk) * toRun));
    this.phase = fraction(this.phase + dt * cycles, 1);
    const crouchRate = Math.max(MIN_CROUCH_RATE, Math.min(MAX_RATE, s.speed / (c.agachado.speed ?? 1)));
    this.crouch += dt * (s.speed < IDLE_SPEED ? 0 : crouchRate);
    this.swim += dt * Math.max(0.6, Math.min(1.5, s.speed / MOVE.swimSpeed));
  }

  private chooseBase(s: AvatarSignals, dt: number): BaseKind {
    const t = this.time;
    const current = this.base.kind;
    // A volta é um evento: saindo da morte, entra em `revivendo` uma vez, mesmo que o primeiro passo depois
    // dela chegue tarde (o avatar fora da tela, ou um quadro longo): o estado entra, já no fim do clipe, e
    // no passo seguinte vem a locomoção. Só pela janela de tempo, a volta se perdia entre dois quadros a ~1 fps.
    const reviving = s.revivedAgo < this.clips.revivendo.duration || (current === "morte" && Number.isFinite(s.revivedAgo));
    const off: BaseKind | undefined = s.dead ? "morte" : reviving ? "revivendo" : s.swimming ? "nadando" : undefined;
    if (off !== undefined) {
      // Morte, volta e nado não passam pelo chão: o pulo e o tempo no ar de antes não valem depois.
      this.air = 0;
      this.jumped = false;
      return off;
    }
    if (!s.grounded) {
      if (this.air === 0 && s.vy > JUMP_VY) this.jumped = true;
      this.air += dt;
      if (this.jumped && this.air < this.clips.pulando.duration) return "pulando";
      if (this.jumped || this.air > AIR_GRACE) return "no-ar";
    } else {
      const landed = this.air > LAND_AIR && s.speed < LAND_SPEED;
      this.air = 0;
      this.jumped = false;
      if (landed) return "aterrissando";
      if (current === "aterrissando" && t - this.base.since < this.clips.aterrissando.duration && s.speed < LAND_SPEED) return "aterrissando";
    }
    // No chão, ou no ar dentro da folga (degraus): o estado de chão.
    if (s.sliding) return "deslizando";
    return s.crouching ? "agachado" : "locomocao";
  }

  /** Quanto o estado atual já entrou (0 a 1). */
  private fade(t: number): number {
    return this.from ? Math.min(1, (t - this.fadeAt) / this.fadeFor) : 1;
  }

  private enter(kind: BaseKind, t: number): void {
    if (kind === this.base.kind) return;
    // Trocando de novo no meio de uma transição, sai do estado que mais aparece (sem pular para o
    // que mal tinha entrado).
    this.from = this.from && this.fade(t) < 0.5 ? this.from : this.base;
    this.entered[kind] = (this.entered[kind] ?? 0) + 1;
    this.fadeAt = t;
    this.fadeFor = kind === "morte" || kind === "pulando" || kind === "revivendo" ? FAST_FADE : FADE;
    this.base = { kind, since: t };
  }

  /** As amostras de um estado do corpo, com peso total `weight`. */
  private samples(base: Base, s: AvatarSignals, t: number, weight: number): ClipSample[] {
    if (weight <= 0) return [];
    const c = this.clips;
    const since = t - base.since;
    const one = (clip: AvatarClipName, time: number): ClipSample[] => [{ clip, time, weight }];
    switch (base.kind) {
      case "locomocao": {
        const walk = c.andando.speed ?? 1;
        const run = c.correndo.speed ?? 3;
        const out: ClipSample[] = [];
        const add = (clip: AvatarClipName, time: number, w: number) => w > 0 && out.push({ clip, time, weight: w * weight });
        if (s.speed < IDLE_SPEED) add("parado", fraction(this.idle, c.parado.duration), 1);
        else if (s.speed < walk) {
          const k = Math.min(1, (s.speed - IDLE_SPEED) / (walk - IDLE_SPEED));
          add("parado", fraction(this.idle, c.parado.duration), 1 - k);
          add("andando", this.phase * c.andando.duration, k);
        } else {
          const k = Math.min(1, (s.speed - walk) / (run - walk));
          add("andando", this.phase * c.andando.duration, 1 - k);
          add("correndo", this.phase * c.correndo.duration, k);
        }
        return out;
      }
      case "agachado": {
        // Como o parado e o andando: abaixo de `MIN_CROUCH_RATE` × a velocidade do clipe, o andar
        // agachado (no piso da reprodução) entra por cima do agachado parado, pela velocidade.
        // O agachado parado segue o relógio do parado (os dois saem do mesmo clipe, então ficam juntos).
        const resting = (w: number): ClipSample => ({ clip: "agachado-parado", time: fraction(this.idle, c["agachado-parado"].duration), weight: w * weight });
        const walking = (w: number): ClipSample => ({ clip: "agachado", time: fraction(this.crouch, c.agachado.duration), weight: w * weight });
        if (s.speed < IDLE_SPEED) return [resting(1)];
        const k = Math.min(1, (s.speed - IDLE_SPEED) / (MIN_CROUCH_RATE * (c.agachado.speed ?? 1) - IDLE_SPEED));
        return k >= 1 ? [walking(1)] : [resting(1 - k), walking(k)];
      }
      case "deslizando":
        return one("agachado-parado", SLIDE_POSE * c["agachado-parado"].duration);
      case "nadando":
        return one("nadando", fraction(this.swim, c.nadando.duration));
      case "no-ar":
        return one("no-ar", fraction(since, c["no-ar"].duration));
      case "revivendo":
        return one("revivendo", clampTime(Math.max(0, s.revivedAgo), c.revivendo.duration));
      case "pulando":
      case "aterrissando":
      case "morte":
        return one(base.kind, clampTime(since, c[base.kind].duration));
    }
  }

  private chooseGesture(s: AvatarSignals): Gesture | undefined {
    if (s.dead || this.base.kind === "morte" || this.base.kind === "revivendo") return undefined;
    if (s.swing > 0) return "golpe";
    if (s.drawing) return "arco";
    if (s.eating) return "comendo";
    if (s.hurtAgo < this.clips.dano.duration) return "dano";
    return undefined;
  }

  private gestureClipTime(gesture: ActiveGesture, s: AvatarSignals, t: number): number {
    const c = this.clips;
    const since = t - gesture.since;
    switch (gesture.kind) {
      case "golpe":
        return swingClipTime(s.swing, c.golpe);
      case "arco":
        return clampTime((since * c.arco.duration) / DRAW_SECONDS, c.arco.duration);
      case "comendo":
        return clampTime((since * c.comendo.duration) / EAT.seconds, c.comendo.duration);
      case "dano":
        return clampTime(Math.max(0, s.hurtAgo), c.dano.duration);
    }
  }
}

/**
 * Instante do golpe para o progresso `p` do ciclo: até o impacto do servidor (`MELEE.impactFraction`)
 * o clipe vai até o impacto dele; depois, até o fim. O clipe estica ou encolhe no ciclo de cada arma.
 */
export function swingClipTime(p: number, golpe: ClipInfo): number {
  const f = MELEE.impactFraction;
  const impact = golpe.impact ?? golpe.duration * f;
  const time = p < f ? (p / f) * impact : impact + ((p - f) / (1 - f)) * (golpe.duration - impact);
  return clampTime(time, golpe.duration);
}

/** Junta amostras do mesmo clipe e da mesma ação (somando o peso e ficando com o instante da mais pesada). */
function merge(samples: ClipSample[]): ClipSample[] {
  const out: ClipSample[] = [];
  for (const s of samples) {
    const twin = out.find((o) => o.clip === s.clip && o.slot === s.slot);
    if (!twin) out.push({ ...s });
    else {
      if (s.weight > twin.weight) twin.time = s.time;
      twin.weight += s.weight;
    }
  }
  return out;
}
